
import { mdiFeather } from '@mdi/js'
import { HelpToggleButton } from 'components/App/components/HelpToggleButton'
import EmptyView from 'components/EmptyView'
import { RecycleDialog } from 'components/Posts/components/RecycleSelector'
import PostFilesDialog from 'components/Posts/PostFilesDialog'
import SimpleLoader from 'components/SimpleLoader'
import { PlannedPost } from 'interfaces'
import { POST_TYPES } from 'interfaces/Content/ContentType'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router'
import CopyPostsPopup from 'routes/publishing/components/CopyPostsPopup'
import { ERROR_NO_PROFILES_SUBTITLE, ERROR_NO_PROFILES_TITLE, PostErrorView } from 'routes/publishing/components/PostErrorView'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators'
import { mergeState, resetComposer } from 'services/compose'
import { getDerivedStateFromPost } from 'services/compose/update/actions'
import { connectedDestinationsSelector } from 'services/destinations'
import { initPlans } from 'services/plan/actions'
import { toggleEmptySlotsVisible, toggleVirtualPostsVisible } from 'services/post'
import { bulkDeletePosts, getPostById, recyclePosts, reorderPosts, shufflePosts, updatePostRecycle } from 'services/post/actions'
import {
  autoPostSlotsSelector,
  emptySlotsVisibleSelector,
  newPostSelector,
  plannedPostsLayoutSelector,
  selectedProfilesFetchIdsSelector,
  selectedProfilesSelector,
  virtualPostsVisibleSelector
} from 'services/post/selectors'
import { publishPosts } from 'services/post/shareNow/actions'
import { checkFeatureAvailability } from 'services/product'
import { message } from 'services/snackbar'
import { FEATURE_POST_COPY, FEATURE_POST_RECYCLE, FEATURE_POST_SHUFFLE } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { MS_IN_SEC, SEC_IN_MIN } from 'utils/constants'
import { recyclePostsSuccess, replacePost, setPosts, updatePostLocation, updateRecyclePost } from './actions'
import PlannedPostsList from './components/PlannedPostsList'
import PostPreviewWithInfo from './components/PostPreviewWithInfo'
import { PostsGridView } from './components/PostsGridView'
import PublishingTopBar from './components/PublishingTopBar'
import { usePlannedPosts } from './hooks/usePlannedPosts'
import styles from './PostViews.pcss'
import { getBulkMovePostsResult, getMovedPostResult, getReorderedPosts } from './utils/reorder'
import { ContentType } from 'shared'

const LIVE_PUBLISHED_CHECK_INTERVAL = MS_IN_SEC * SEC_IN_MIN

function PostsRoute() {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const viewFilter = params.type === 'all' ? 'planned' : params.type
  // EXPL: source is used for some of the bulk actions to determine the context of the posts. Can be 'bucket' or 'no-bucket'
  const source = viewFilter === 'buckets' ? 'bucket' : viewFilter === 'no-bucket' ? 'no-bucket' : undefined
  const selectedProfilesIds = useSelector(selectedProfilesSelector)
  const selectedProfilesFetchIds = useSelector(selectedProfilesFetchIdsSelector)
  const newPost = useSelector(newPostSelector)
  const autoPostSlots = useSelector(autoPostSlotsSelector)
  const connectedProfiles = useSelector(connectedDestinationsSelector)

  const [loadingOverlayActive, setLoadingOverlayActive] = useState(false)

  const onBeforeFetch = useCallback(() => {
    setLoadingOverlayActive(true)
  }, [])

  const onPostsFetched = useCallback(() => {
    setLoadingOverlayActive(false)
    setIsEmptyViewClosed(false)
  }, [])

  const [search, setSearch] = useState<{ query: string, types: ContentType[], searchInSummary: boolean }>(
    { query: '', types: POST_TYPES, searchInSummary: true }
  )

  useEffect(() => {
    if (location.state?.postsSearchQuery) {
      setSearch(current => ({ ...current, query: location.state.postsSearchQuery as string }))
      navigate(location.pathname, { state: undefined })
    }
  }, [location.state?.postsSearchQuery, location.pathname, navigate])
  const {
    posts,
    postsMap,
    loading,
    hasNonEmptyPosts,
    totalPostsCount,
    hasPendingPosts,
    loadNextPage,
    refresh,
    postsDispatch
  } = usePlannedPosts(search, viewFilter, onBeforeFetch, onPostsFetched)

  // EXPL: keep latest posts/slots in refs so item callbacks stay referentially
  // stable across posts updates and don't force every list item to re-render
  const postsRef = useRef(posts)
  const autoPostSlotsRef = useRef(autoPostSlots)
  useEffect(() => {
    postsRef.current = posts
  }, [posts])
  useEffect(() => {
    autoPostSlotsRef.current = autoPostSlots
  }, [autoPostSlots])

  const [selectedPostsIds, setSelectedPostsIds] = useState<Record<string, boolean> | 'all'>({})
  const [postInMediaPreviewDialog, setPostInMediaPreviewDialog] = useState<PlannedPost | null>(null)
  const [postInPreview, setPostInPreview] = useState<PlannedPost | null>(null)
  const [withPreview, setWithPreview] = useState(false)
  const [postsToCopy, setPostsToCopy] = useState<PlannedPost[]>([])
  const activeView = useSelector(plannedPostsLayoutSelector)
  const emptySlotsVisible = useSelector(emptySlotsVisibleSelector)
  const virtualPostsVisible = useSelector(virtualPostsVisibleSelector)
  const [recycleDialogOpen, setRecycleDialogOpen] = useState(false)
  const [isEmptyViewClosed, setIsEmptyViewClosed] = useState(false)

  const getScheduledPostsSelection = useCallback((): 'all' | 'some' | 'none' => {
    const ids = selectedPostsIds === 'all' ? Array.from(postsMap.keys()) : Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id])
    if (ids.length === 0) return 'none'
    let hasScheduled = false
    let hasUnscheduled = false
    for (const id of ids) {
      const post = postsMap.get(id)
      if (post && !post.autoPost && Boolean(post.time)) {
        hasScheduled = true
      } else {
        hasUnscheduled = true
      }
      if (hasScheduled && hasUnscheduled) return 'some'
    }
    return hasScheduled ? 'all' : 'none'
  }, [postsMap, selectedPostsIds])

  const closeEmptyView = () => {
    setIsEmptyViewClosed(true)
  }

  useEffect(() => {
    if (location.state?.updatedPostHash) {
      const updatedPostId = location.state.updatedPostHash.split('-')[0]
      dispatch(getPostById(updatedPostId))
        .unwrap()
        .then((post: any) => {
          postsDispatch(replacePost(post))
        })
        .catch(refresh)
    }
    if (location.state?.search) {
      setSearch(location.state.search)
    }
  }, [location.state?.search, postsDispatch, location.state?.updatedPostHash, dispatch, refresh])

  const delete$ = useRef<Subject<{
    postIds: string[], ppids: string[], all: boolean, query?: string, types?: ContentType[]
  }>>(undefined)
  const reorder$ = useRef<Subject<{ ids: string[], moveTo?: 'top' | 'bottom' }>>(undefined)
  const share$ = useRef<Subject<{
    postIds: string[],
    ppids: string[],
    all: boolean,
    query?: string,
    types?: ContentType[],
    bucketId?: string
  }>>(undefined)
  const shuffle$ = useRef<Subject<{ postIds: string[], ppids: string[], all?: boolean }>>(undefined)
  const batchRecycle$ = useRef<Subject<{
    postIds: string[], ppids: string[], all?: boolean, query?: string, types?: ContentType[], value?: boolean | Date | number
  }>>(undefined)
  const recycle$ = useRef<Subject<{ postId: string, value: boolean | Date | number, refresh?: boolean }>>(undefined)
  const livePublishedCheckTimer = useRef<any>(undefined)
  const selectionRef = useRef<{ postIds: string[], profileIds: string[], ppids: string[], all: boolean }>(undefined)

  useEffect(() => {
    dispatch(initPlans())
  }, [dispatch])

  const onEdit = useCallback((post: PlannedPost) => {
    dispatch(resetComposer())
    dispatch(mergeState(getDerivedStateFromPost(post, 'planned', viewFilter as any)))
    const stateParams = {
      floating: true,
      modal: 'compose',
      opener: location.pathname + location.search,
      search
    }
    navigate('/composer', { state: { ...stateParams, background: location } })
  }, [dispatch, navigate, viewFilter, search, location])

  const onFilter = useCallback((query: string, types: ContentType[], searchInSummary: boolean) => {
    setSearch({ query, types, searchInSummary })
    setSelectedPostsIds({})
  }, [])

  const nonEmptyPosts = useMemo(() => {
    return posts.filter(p => !p.isEmpty)
  }, [posts])

  const onToggleEmptySlotsVisible = useCallback(() => {
    dispatch(toggleEmptySlotsVisible())
  }, [dispatch])

  const onToggleVirtualPostsVisible = useCallback(() => {
    dispatch(toggleVirtualPostsVisible())
  }, [dispatch])

  const onTogglePreview = () => {
    setWithPreview(current => !current)
  }

  // Check for newly created/updated posts while posts page is active. Reload posts to fetch the new records
  useEffect(() => {
    if ((newPost || location.state?.refresh) && !location.state?.updatedPostHash) {
      refresh()
    }
  }, [newPost, location.state?.refresh, refresh, location.state?.updatedPostHash])

  // Initialize a timer that checks for posts that were published while the page was active and mark them as "published"
  useEffect(() => {
    if (livePublishedCheckTimer.current) {
      clearInterval(livePublishedCheckTimer.current)
    }

    livePublishedCheckTimer.current = setInterval(() => {
      const now = Date.now()
      let hasUpdated = false
      const updatedPosts = posts.map(p => {
        const published = new Date(p.time).getTime() - now < 0
        // EXPL: only rebuild posts whose published state actually flipped, preserving
        // refs for the rest so memoized list items don't re-render every minute
        if (published === Boolean(p.published)) {
          return p
        }
        hasUpdated = true
        return { ...p, published }
      })
      if (hasUpdated) {
        postsDispatch(setPosts(updatedPosts))
      }
    }, LIVE_PUBLISHED_CHECK_INTERVAL)

    return () => {
      clearInterval(livePublishedCheckTimer.current)
    }
  }, [posts, postsDispatch])

  // Clear posts and preview when profile selection changes
  useEffect(() => {
    postsDispatch(setPosts([]))
    setPostInPreview(null)
  }, [postsDispatch, selectedProfilesFetchIds])

  useEffect(() => {
    delete$.current = new Subject()
    delete$.current
      .pipe(
        tap(() => {
          setLoadingOverlayActive(true)
        }),
        mergeMap(({ ppids, postIds, all, query, types }) => {
          const type = ['queued', 'scheduled', 'planned'].includes(viewFilter as any) ? viewFilter : 'planned'
          return dispatch(bulkDeletePosts(postIds, ppids, type as any, all, undefined, query, types, source, refresh))
            .pipe(catchError((error: Error) => {
              dispatch(message(intl.formatMessage({ id: 'post.notifications.delete-posts-error' }, { error: error.message }), 'error'))
              return observableOf({ error })
            }))
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setSelectedPostsIds({})
          refresh()
        } else {
          setLoadingOverlayActive(false)
        }
      })

    return () => delete$.current?.unsubscribe()
  }, [dispatch, viewFilter, intl, refresh, source])

  useEffect(() => {
    reorder$.current = new Subject()
    reorder$.current.pipe(
      tap(() => {
        setLoadingOverlayActive(true)
      }),
      exhaustMap(({ ids, moveTo }) => dispatch(reorderPosts(ids, moveTo)).pipe(catchError(() => {
        dispatch(message(intl.formatMessage({ id: 'post.notifications.update-failed' }), 'error'))
        return observableOf({ error: true })
      })))
    )
      .subscribe((res) => {
        if (res.error) {
          refresh()
        }
        setLoadingOverlayActive(false)
      })

    return () => reorder$.current?.unsubscribe()
  }, [dispatch, intl, refresh])

  useEffect(() => {
    shuffle$.current = new Subject()
    shuffle$.current.pipe(
      tap(() => {
        setLoadingOverlayActive(true)
        dispatch(message(intl.formatMessage({ id: 'post.notifications.shuffling-posts' })))
      }),
      mergeMap(({ postIds, ppids, all }) => {
        const postType = ['no-bucket', 'buckets'].includes(viewFilter as any) ? 'planned' : viewFilter
        return dispatch(shufflePosts(postIds, ppids, postType as any, all, undefined, source))
          .pipe(catchError(() => {
            return observableOf({ error: true })
          }))
      })
    )
      .subscribe(response => {
        setLoadingOverlayActive(false)
        if (!response.error) {
          setSelectedPostsIds({})
          refresh()
        }
      })

    return () => shuffle$.current?.unsubscribe()
  }, [dispatch, viewFilter, intl, refresh, source])

  useEffect(() => {
    batchRecycle$.current = new Subject()
    batchRecycle$.current.pipe(
      tap(() => {
        dispatch(message(intl.formatMessage({ id: 'post.notifications.recycling-posts' })))
      }),
      mergeMap(({ ppids, postIds, all, query, types, value }) => {
        const postType = ['no-bucket', 'buckets'].includes(viewFilter as any) ? 'planned' : viewFilter
        return dispatch(recyclePosts(postIds, ppids, postType as any, all, undefined, query, types, value, source))
          .pipe(catchError((e) => {
            const msg = e.response?.error.message || intl.formatMessage({ id: 'errors.generic' })
            dispatch(message(msg, 'error'))
            return observableOf({ error: true })
          }))
      })
    )
      .subscribe(response => {
        if (!response.error) {
          setSelectedPostsIds({})
          postsDispatch(recyclePostsSuccess(response.postIds, response.all))
          for (const postId of response.postIds) {
            postsDispatch(updateRecyclePost(postId, response.recycle))
          }
        }
      })

    return () => batchRecycle$.current?.unsubscribe()
  }, [dispatch, viewFilter, intl, postsDispatch, source])

  useEffect(() => {
    recycle$.current = new Subject()
    recycle$.current
      .pipe(
        mergeMap(({ postId, value, refresh }) => dispatch(updatePostRecycle(postId, value))
          .pipe(
            catchError((e) => {
              const msg = e.response?.error.message || intl.formatMessage({ id: 'errors.generic' })
              dispatch(message(msg, 'error'))
              return observableOf({ error: true })
            }),
            map((response: any) => ({ ...response, refresh }))
          )
        )
      )
      .subscribe(response => {
        if (!response.error) {
          setSelectedPostsIds({})
          postsDispatch(updateRecyclePost(response.postId, response.value))
        }
        if (response.refresh) {
          refresh()
        }
      })

    return () => recycle$.current?.unsubscribe()
  }, [dispatch, postsDispatch, intl, refresh])

  useEffect(() => {
    share$.current = new Subject()
    share$.current
      .pipe(
        tap(() => {
          setLoadingOverlayActive(true)
        }),
        mergeMap(({ ppids, postIds, all, query, types, bucketId }) => {
          return dispatch(publishPosts(postIds, ppids, 'planned', all, bucketId, query, types))
            .pipe(catchError((error: Error) => {
              dispatch(message(intl.formatMessage({ id: 'notifications.error-w-message' }, { error: error.message }), 'error'))
              return observableOf({ error })
            }))
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setSelectedPostsIds({})
          dispatch(message(intl.formatMessage({ id: 'notifications.post-posted' }), 'success'))
          refresh()
        }
        setLoadingOverlayActive(false)
      })

    return () => share$.current?.unsubscribe()
  }, [dispatch, intl, refresh])

  useEffect(() => {
    const profileIds = selectedProfilesFetchIds.split(',')
    const ppids = Object.values(connectedProfiles).filter(p => profileIds.includes(p.idNew)).map(p => p.ppid)
    if (selectedPostsIds === 'all') {
      selectionRef.current = {
        all: true,
        postIds: posts.filter(p => !p.isVirtual).map(p => p.id),
        profileIds,
        ppids
      }
    } else {
      selectionRef.current = {
        all: false,
        profileIds,
        ppids,
        postIds: Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id])
      }
    }
  }, [selectedPostsIds, posts, selectedProfilesFetchIds, connectedProfiles])

  const previewSelection = useMemo(() => {
    const selectedPostIndex = postInPreview
      ? nonEmptyPosts.findIndex((p: any) => p.id === postInPreview.id)
      : -1
    let hasNext = false
    let hasPrev = false

    if (selectedPostIndex !== -1) {
      hasNext = selectedPostIndex < nonEmptyPosts.length - 1
      hasPrev = selectedPostIndex >= 1
    }
    return {
      selectedPostIndex,
      hasNext,
      hasPrev
    }
  }, [postInPreview, nonEmptyPosts])

  useEffect(() => {
    if (withPreview) {
      const firstNonEmptyPost = posts.find(p => !p.isEmpty)

      setPostInPreview(current => {
        const postInPreview = current ? posts.find(p => p.id === current.id) : null
        if (!postInPreview && firstNonEmptyPost) {
          return firstNonEmptyPost
        }
        if (!firstNonEmptyPost) {
          return null
        }
        return postInPreview || null
      })
    } else {
      setPostInPreview(null)
    }
  }, [withPreview, posts])

  const toggleAllPostsSelected = useCallback(() => {
    setSelectedPostsIds(current => current === 'all' ? {} : 'all')
  }, [])

  const onDeletePost = useCallback((id: string) => {
    delete$.current?.next({
      postIds: [id],
      ppids: selectionRef.current?.ppids || [],
      all: false
    })
  }, [])

  const movePost = useCallback((movedPost: PlannedPost, position: 'top' | 'bottom' | 'up' | 'down') => {
    const reordered = getMovedPostResult(movedPost, position, postsRef.current, autoPostSlotsRef.current)
    if (reordered) {
      const { posts: updatedPosts, ids, moveTo } = reordered
      postsDispatch(setPosts(updatedPosts))
      reorder$.current?.next({ ids, moveTo })
    }
  }, [postsDispatch])

  const onReorderPosts = useCallback((movedPost: PlannedPost, targetPost: PlannedPost) => {
    const reordered = getReorderedPosts(movedPost, targetPost, postsRef.current, autoPostSlotsRef.current)
    if (reordered) {
      const { posts: updatedPosts, ids } = reordered
      postsDispatch(setPosts(updatedPosts))
      reorder$.current?.next({ ids })
    }
  }, [postsDispatch])

  const onRecycleChange = useCallback((id: string, value: number | boolean | Date, forceRefresh?: boolean) => {
    const recycleAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_RECYCLE))
    if (!recycleAvailable) {
      return
    }
    const shouldRefresh = forceRefresh || virtualPostsVisible
    recycle$.current?.next({ postId: id, value, refresh: shouldRefresh })
  }, [dispatch, virtualPostsVisible])

  const onCopyPost = useCallback((post: PlannedPost) => {
    const copyAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_COPY))
    if (copyAllowed) {
      setPostsToCopy([post])
    }
  }, [dispatch])

  const onCopyPosts = useCallback(() => {
    const copyAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_COPY))
    if (copyAllowed) {
      if (selectionRef.current?.all) {
        setPostsToCopy(nonEmptyPosts)
      } else if (selectionRef.current?.postIds) {
        setPostsToCopy(nonEmptyPosts.filter(p => selectionRef.current?.postIds.includes(p.id)))
      }
    }
  }, [dispatch, nonEmptyPosts])

  const closeRecycleDialog = () => {
    setRecycleDialogOpen(false)
  }

  const onBulkRecycle = (value: boolean | Date | number) => {
    batchRecycle$.current?.next({ ...(selectionRef.current as any), query: search.query, types: search.types, value })

    closeRecycleDialog()
  }

  const onBatchRecycle = useCallback(() => {
    const scheduledSelection = getScheduledPostsSelection()
    if (scheduledSelection === 'all' || scheduledSelection === 'some') {
      dispatch(message('Scheduled posts cannot be recycled. Please select queued posts only.', 'error'))
      return
    }
    setRecycleDialogOpen(true)
  }, [getScheduledPostsSelection, dispatch])

  const onPostUpdated = (post: PlannedPost) => {
    postsDispatch(updatePostLocation(post.id, post.details?.location))
  }

  const onShuffle = useCallback(() => {
    const shuffleAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_SHUFFLE))
    if (!shuffleAvailable) {
      return
    }

    const scheduledSelection = getScheduledPostsSelection()
    if (scheduledSelection === 'all' || scheduledSelection === 'some') {
      dispatch(message('Scheduled posts cannot be shuffled. Please select queued posts only.', 'error'))
      return
    }

    const selection = selectionRef.current
    if (selection && (selection.all || selection.postIds.length > 0)) {
      shuffle$.current?.next(selection)
    }
  }, [dispatch, getScheduledPostsSelection])

  const togglePostSelected = useCallback((id: string) => {
    setSelectedPostsIds(current => {
      if (current === 'all') {
        return postsRef.current.filter(p => !p.isEmpty && !p.isVirtual).reduce((selection: Record<string, boolean>, post) => {
          selection[post.id] = post.id !== id
          return selection
        }, {})
      }
      return { ...current, [id]: !current[id] }
    })
  }, [])

  const closePostMediaPreviewDialog = useCallback(() => {
    setPostInMediaPreviewDialog(null)
  }, [])

  const deleteSelectedPosts = useCallback(() => {
    delete$.current?.next({
      ...(selectionRef.current as any),

      query: search.query,
      types: search.types
    })
  }, [search.query, search.types])

  const closeCopyPostsPopup = useCallback(() => {
    setPostsToCopy([])
    setSelectedPostsIds({})
  }, [])

  const onPostClick = useCallback((post: PlannedPost) => {
    setWithPreview(true)
    setPostInPreview(post)
  }, [])

  const selectNextPostForPreview = useCallback(() => {
    if (previewSelection.hasNext) {
      const next = nonEmptyPosts[previewSelection.selectedPostIndex + 1]
      setPostInPreview(next)
    }
  }, [previewSelection, nonEmptyPosts])

  const selectPrevPostForPreview = useCallback(() => {
    if (previewSelection.hasPrev) {
      const prev = nonEmptyPosts[previewSelection.selectedPostIndex - 1]
      setPostInPreview(prev)
    }
  }, [previewSelection, nonEmptyPosts])

  const deletePost = useCallback((post: PlannedPost) => {
    onDeletePost(post.id)
  }, [onDeletePost])

  const visiblePosts = useMemo(() => {
    if (emptySlotsVisible) {
      return posts
    }
    return posts.filter(p => !p.isEmpty)
  }, [posts, emptySlotsVisible])

  const showEmptyPostsMessage = !loading && selectedProfilesIds.length > 0 && nonEmptyPosts.length === 0 && totalPostsCount === 0

  const selectedPostsCount = useMemo(() => {
    return selectedPostsIds !== 'all'
      ? Object.values(selectedPostsIds).filter(Boolean).length
      : totalPostsCount
  }, [totalPostsCount, selectedPostsIds])

  const onShareNow = useCallback((post: PlannedPost) => {
    share$.current?.next({ all: false, postIds: [post.id], ppids: [post.ppPageId], bucketId: post.bucketId })
  }, [])

  const bulkMoveToTop = useCallback(() => {
    if (selectedPostsIds === 'all') {
      setSelectedPostsIds({})
      return
    }
    const scheduledSelection = getScheduledPostsSelection()
    if (scheduledSelection === 'all' || scheduledSelection === 'some') {
      dispatch(message('Scheduled posts cannot be moved. Please select queued posts only.', 'error'))
      return
    }
    const movedPosts = Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id])
      .map(id => postsMap.get(id)).filter(Boolean) as PlannedPost[]
    const result = getBulkMovePostsResult(movedPosts, 'top', posts, autoPostSlots)
    if (result) {
      const { posts: updatedPosts, ids } = result
      postsDispatch(setPosts(updatedPosts))
      reorder$.current?.next({ ids })
      setSelectedPostsIds({})
    }
  }, [selectedPostsIds, posts, autoPostSlots, postsMap, getScheduledPostsSelection, dispatch, postsDispatch])

  const bulkMoveToBottom = useCallback(() => {
    if (selectedPostsIds === 'all') {
      setSelectedPostsIds({})
      return
    }
    const scheduledSelection = getScheduledPostsSelection()
    if (scheduledSelection === 'all' || scheduledSelection === 'some') {
      dispatch(message('Scheduled posts cannot be moved. Please select queued posts only.', 'error'))
      return
    }
    const movedPosts = Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id])
      .map(id => postsMap.get(id)).filter(Boolean) as PlannedPost[]
    const result = getBulkMovePostsResult(movedPosts, 'bottom', posts, autoPostSlots)
    if (result) {
      const { posts: updatedPosts, ids } = result
      postsDispatch(setPosts(updatedPosts))
      reorder$.current?.next({ ids })
      setSelectedPostsIds({})
    }
  }, [selectedPostsIds, posts, autoPostSlots, postsMap, getScheduledPostsSelection, dispatch, postsDispatch])

  const bulkActionsProps = useMemo(() => {
    const deletePostsConfirmMessage = selectedPostsIds === 'all'
      ? intl.formatMessage({ id: 'post.delete-all-confirm' }, { count: totalPostsCount, type: 'planned' })
      : undefined

    const withBulkMove = selectedPostsIds !== 'all' && selectedPostsCount > 1 && selectedPostsCount < totalPostsCount

    return {
      hidePostActions: !hasNonEmptyPosts,
      withDelete: true,
      withRecycle: viewFilter !== 'scheduled',
      withShuffle: viewFilter !== 'scheduled',
      withCopy: true,
      allSelected: selectedPostsIds === 'all',
      disabled: selectedPostsCount === 0,
      selectedCount: selectedPostsCount,
      deleteAllConfirmMessage: deletePostsConfirmMessage,
      skipDeleteConfirm: true, // undo snackbar available (onRestored passed to bulkDeletePosts)
      onToggleAllSelected: toggleAllPostsSelected,
      onBulkDeletePosts: deleteSelectedPosts,
      onBulkRecycle: onBatchRecycle,
      onShuffle,
      onCopy: onCopyPosts,
      onMoveToTop: withBulkMove ? bulkMoveToTop : undefined,
      onMoveToBottom: withBulkMove ? bulkMoveToBottom : undefined
    }
  }, [
    intl,
    hasNonEmptyPosts,
    selectedPostsIds,
    selectedPostsCount,
    totalPostsCount,
    viewFilter,
    toggleAllPostsSelected,
    deleteSelectedPosts,
    onBatchRecycle,
    onCopyPosts,
    onShuffle,
    bulkMoveToTop,
    bulkMoveToBottom
  ])

  return (
    <div className={styles.page}>
      <PublishingTopBar
        withViewPicker
        withPreviewToggle
        withPostsViewToggle
        withEmptySlotsToggle={viewFilter !== 'scheduled'}
        postsView={`posts/planned${params.type ? '/' + params.type : ''}`}
        activeTypes={search.types}
        showPreview={withPreview}
        emptySlotsVisible={emptySlotsVisible}
        totalCount={totalPostsCount}
        virtualPostsVisible={virtualPostsVisible}
        showPendingLink={!loading && hasPendingPosts && viewFilter !== 'pending'}
        onToggleEmptySlotsVisible={onToggleEmptySlotsVisible}
        onToggleVirtualPostsVisible={onToggleVirtualPostsVisible}
        onTogglePreview={onTogglePreview}
        onFilter={onFilter}
        children={<HelpToggleButton />}
      />
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {loadingOverlayActive && (
            <div className={styles['loading-overlay']}>
              <SimpleLoader small />
            </div>
          )}
          {activeView === 'list' && (
            <PlannedPostsList
              posts={visiblePosts}
              selectedPosts={selectedPostsIds}
              activePreviewPostId={postInPreview?.id}
              bulkActionsProps={bulkActionsProps}
              // disable move up/down when search/filtering is active
              hideMoveUpDown={Boolean(search.query) || search.types.length !== POST_TYPES.length}
              onLoadNextPage={loadNextPage}
              onTogglePostSelected={togglePostSelected}
              onDeletePost={onDeletePost}
              onEditPost={onEdit}
              onCopyPost={onCopyPost}
              onMediaPreviewBtnClick={setPostInMediaPreviewDialog}
              onMovePost={movePost}
              onPostOrderChanged={onReorderPosts}
              onRecycleChange={onRecycleChange}
              onPostClick={onPostClick}
              onShareNow={onShareNow}
            />
          )}
          {activeView === 'grid' && (
            <PostsGridView
              posts={visiblePosts}
              selectedPosts={selectedPostsIds}
              bulkActionsProps={bulkActionsProps}
              withDragAndDrop
              onPostOrderChanged={onReorderPosts}
              onLoadNextPage={loadNextPage}
              onPostClick={onPostClick}
              onTogglePostSelected={togglePostSelected}
              onDeletePost={onDeletePost}
              onEditPost={onEdit}
              onCopyPost={onCopyPost}
              onShareNow={onShareNow}
            />
          )}
          {!loading && selectedProfilesIds.length === 0 && (
            <PostErrorView title={ERROR_NO_PROFILES_TITLE} subtitle={ERROR_NO_PROFILES_SUBTITLE} />
          )}
          {showEmptyPostsMessage && !loading && !isEmptyViewClosed && (
            <EmptyView
              icon={mdiFeather}
              title={`No ${viewFilter} posts`}
              subtitle={(
                <div>
                  <NavLink to={{ pathname: '/composer' }} state={{ floating: true, modal: 'compose', opener: location.pathname }}>
                    Create a new post
                  </NavLink>
                  &nbsp;or <NavLink to="/content/search/content">search for content</NavLink>
                </div>
              )}
              backdrop
              onClose={closeEmptyView}
            />
          )}
          {loading && !loadingOverlayActive && <SimpleLoader small />}
        </div>
        <div
          className={`${styles['preview-box']} ${withPreview ? styles.visible : ''}`}
        >
          {postInPreview && (
            <PostPreviewWithInfo
              post={postInPreview}
              hasNextPost={previewSelection.hasNext}
              hasPreviousPost={previewSelection.hasPrev}
              className={styles.preview}
              onClose={onTogglePreview}
              onSelectNext={selectNextPostForPreview}
              onSelectPrevious={selectPrevPostForPreview}
              skipDeleteConfirm
              onDelete={deletePost}
              onMove={movePost}
              onEdit={onEdit}
              onCopy={onCopyPost}
              onRecycleChange={onRecycleChange}
              onPostUpdated={onPostUpdated}
            />
          )}
        </div>
        <PostFilesDialog
          active={Boolean(postInMediaPreviewDialog)}
          images={postInMediaPreviewDialog?.images || []}
          onClose={closePostMediaPreviewDialog}
        />
      </div>
      <RecycleDialog open={recycleDialogOpen} onClose={closeRecycleDialog} onSubmit={onBulkRecycle} />
      <CopyPostsPopup
        open={postsToCopy.length > 0}
        posts={postsToCopy}
        allSelected={Boolean(selectionRef.current?.all)}
        query={search.query}
        types={search.types}
        postType={!['buckets', 'no-bucket'].includes(viewFilter as any) ? viewFilter as any : 'planned'}
        bucketView={viewFilter === 'buckets'}
        source={source}
        onClose={closeCopyPostsPopup}
        onCopyStarted={closeCopyPostsPopup}
        onCopyFinished={refresh}
      />
    </div>
  )
}

export default memo(PostsRoute)
